import { Box, Divider, Stack, Tooltip, Typography } from "@mui/material";
import { Drawer, Input, SelectField } from "components";
import {
  useAddCustomerSites,
  useGetAllCountries,
  useGetAllStateByCountryId,
  useGetCustomerDetails,
  useGetUserDetails,
  useUpdateCustomerSites,
} from "controller";
import { CustomToggleButton } from "library/base";
import CustomCheckBox from "library/base/custom-checkbox";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import theme from "utilz/theme/theme";
import { getFullName } from "utilz/helpers";
import { useGetDoesHaveCustomerFullAccess } from "pages/modules/customer/context";

const schema = yup.object({
  site_name: yup.string().required("Site name is required").trim(),
  contact_person: yup.string().nullable().optional().trim(),
  email: yup.string().email("Invalid email format").nullable().optional(),
  contact_number: yup.string().nullable(),
  // .trim()
  // .matches(/^[0-9]*$/, {
  //   message: "Invalid contact number",
  //   excludeEmptyString: true,
  // })
  // .nullable()
  // .optional(),
  address_1: yup.string().trim().required("Address is required"),
  address_2: yup.string(),
  zip_code: yup
    .string()
    .nullable()
    .optional()
    .trim()
    .max(25, "Zip code must be at most 25 characters"),
  is_contact_person_as_primary_contact: yup.boolean(),
  is_active: yup.boolean(),
  street: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  city: yup
    .string()
    .trim()
    .nullable()
    .optional()
    .max(50, "City must be at most 50 characters")
    .matches(/\p{L}/u, {
      message: "City must contain at least one letter",
      excludeEmptyString: true,
    }),
});

function SiteAddDrawer({
  open,
  onClose,
  drawerData,
}: {
  open: boolean;
  onClose: () => void;
  drawerData?: any;
}) {
  const haveFullAccess = useGetDoesHaveCustomerFullAccess();
  const [isContactPersonAsPrimaryContact, setIsContactPersonAsPrimaryContact] =
    useState(false);
  const [shouldAutoSync, setShouldAutoSync] = useState(false);
  const { id: customerIdParams }: any = useParams();
  const {
    mutateAsync: addCustomerSites,
    isLoading: isAddCustomerSitesLoading,
  } = useAddCustomerSites();
  const {
    mutateAsync: updateCustomerSites,
    isLoading: isUpdateCustomerSitesLoading,
  } = useUpdateCustomerSites();
  const { data: countries } = useGetAllCountries();
  const [drawerStatus, setDrawerStatus] = useState(false);
  const [primaryButtonText, setPrimaryButtonText] = useState("Create");
  const { id } = useParams();
  const [primaryContactMappingId, setPrimaryContactmappingId] =
    useState<any>(null);
  const [title, setTitle] = useState<string>("Add Site");

  const {
    data: customerDetailsData,
    refetch: refetchCustomerDetails,
    isLoading: customerDetailsLoading,
    isError: customerDetailsError,
  } = useGetCustomerDetails({
    customerId: id,
  });
  const { data: userDetails } = useGetUserDetails(
    primaryContactMappingId || "",
  );
  useEffect(() => {
    setPrimaryContactmappingId(customerDetailsData?.primary_contact_mapping_id);
    return () => {
      setPrimaryContactmappingId(null);
    };
  }, [customerDetailsData]);

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      site_name: "",
      contact_person: "",
      email: "",
      contact_number: "",
      address_1: "",
      address_2: "",
      country: "",
      state: "",
      city: "",
      street: "",
      zip_code: "",
      is_active: true,
      is_contact_person_as_primary_contact: isContactPersonAsPrimaryContact,
    },
    resolver: yupResolver(schema),
  });
  const watchCountry = watch("country");
  const { data: provinces } = useGetAllStateByCountryId(watchCountry);

  useEffect(() => {
    // Only auto-sync if we are in create mode OR if it was a manual toggle
    const isCreateMode = drawerData?.mode === "create";
    if (
      isContactPersonAsPrimaryContact &&
      userDetails &&
      (isCreateMode || shouldAutoSync)
    ) {
      setValue(
        "contact_person",
        getFullName(userDetails?.first_name, "", userDetails?.last_name) || "",
      );
      setValue("email", userDetails?.email || "");
      setValue("contact_number", userDetails?.office_phone || "");
      // setValue("country", userDetails?.country_id || "");
      // setValue("state", userDetails?.state_id || "");
      // setValue("city", userDetails?.city || "");
      // setValue("street", userDetails?.street || "");
      // setValue("zip_code", userDetails?.zip_code || "");
      // setValue("address_1", userDetails?.address_1 || "");
      // setValue("address_2", userDetails?.address_2 || "");
      setValue("country", customerDetailsData?.country_id || "");
      setValue("state", customerDetailsData?.state_id || "");
      setValue("city", customerDetailsData?.city || "");
      setValue("street", customerDetailsData?.street || "");
      setValue("zip_code", customerDetailsData?.zip_code || "");
      setValue("address_1", customerDetailsData?.address_1 || "");
      setValue("address_2", customerDetailsData?.address_2 || "");

      // Reset the auto-sync flag after sync is done
      if (shouldAutoSync) setShouldAutoSync(false);
    }
  }, [
    isContactPersonAsPrimaryContact,
    userDetails,
    shouldAutoSync,
    drawerData?.mode,
    customerDetailsData,
  ]);

  const handleSitesSave = handleSubmit(async (data: any) => {
    try {
      if (drawerData?.mode == "edit" && drawerData?.data) {
        const siteId = drawerData?.data?.mapping_id;
        const updateSite = await updateCustomerSites({
          siteId,
          customerId: customerIdParams,
          data,
        });
        if (updateSite?.mapping_id) {
          toast.success("Site updated successfully");
        } else {
          toast.error("Unable to update site at the moment.Try again later");
        }
        onClose();
        reset();
      } else if (drawerData?.mode == "create") {
        const createSite = await addCustomerSites({ customerIdParams, data });
        onClose();
        if (createSite?.mapping_id) {
          toast.success("Site created successfully");
        } else {
          toast.error("Unable to Add site at the moment.Try again later");
        }
        reset();
      }
    } catch (error: any) {
      const errorMessage = error?.message || "Something went wrong";
      toast.error(errorMessage);
    }
  });
  const watchIsactive = watch("is_active");

  useEffect(() => {
    if (drawerData?.open) {
      if (drawerData?.mode === "edit" && drawerData?.data) {
        setTitle(haveFullAccess ? "Edit Site" : "View Site");
        setPrimaryButtonText("Save");
        const isSameAsPrimary =
          !!drawerData?.data?.is_contact_person_as_primary_contact;
        setIsContactPersonAsPrimaryContact(isSameAsPrimary);
        reset({
          site_name: drawerData?.data?.site_name || "",
          contact_person: drawerData?.data?.contact_person || "",
          email: drawerData?.data?.email || "",
          contact_number: drawerData?.data?.contact_number || "",
          address_1: drawerData?.data?.address_1 || "",
          address_2: drawerData?.data?.address_2 || "",
          country: drawerData?.data?.country_id || "",
          state: drawerData?.data?.state_id || "",
          city: drawerData?.data?.city || "",
          street: drawerData?.data?.street || "",
          zip_code: drawerData?.data?.zip_code || "",
          is_active: !!drawerData?.data?.is_active,
          is_contact_person_as_primary_contact: isSameAsPrimary,
        });
      } else {
        setTitle("Add Site");
        setPrimaryButtonText("Create");
        const isSameAsPrimary = !!primaryContactMappingId;
        setIsContactPersonAsPrimaryContact(isSameAsPrimary);
        if (isSameAsPrimary) {
          setShouldAutoSync(true);
        }
        reset({
          site_name: "",
          contact_person: "",
          email: "",
          contact_number: "",
          //           address_1: "",
          // address_2: "",
          // country: "",
          // state: "",
          // city: "",
          // street: "",
          // zip_code: "",
          address_1: customerDetailsData?.address_1 || "",
          address_2: customerDetailsData?.address_2 || "",
          country: customerDetailsData?.country_id || "",
          state: customerDetailsData?.state_id || "",
          city: customerDetailsData?.city || "",
          street: customerDetailsData?.street || "",
          zip_code: customerDetailsData?.zip_code || "",
          ////
          is_active: true,
          is_contact_person_as_primary_contact: isSameAsPrimary,
        });
      }
      setDrawerStatus(true);
    } else {
      setDrawerStatus(false);
      setIsContactPersonAsPrimaryContact(false);
      setShouldAutoSync(false);
      reset();
    }
  }, [
    drawerData?.open,
    drawerData?.mode,
    drawerData?.data,
    reset,
    primaryContactMappingId,
  ]);

  const handleIsContactPersonAsPrimaryContact = () => {
    const newVal = !isContactPersonAsPrimaryContact;
    setIsContactPersonAsPrimaryContact(newVal);
    setValue("is_contact_person_as_primary_contact", newVal, {
      shouldDirty: true,
    });
    if (newVal) {
      setShouldAutoSync(true);
    } else {
      setShouldAutoSync(false);
      setValue("contact_person", "");
      setValue("email", "");
      setValue("contact_number", "");
      // setValue("address_1", "");
      // setValue("address_2", "");
      // setValue("country", "");
      // setValue("state", "");
      // setValue("city", "");
      // setValue("street", "");
      // setValue("zip_code", "");
    }
  };

  return (
    <Drawer
      title={title}
      drawerStyle={{ width: "404px" }}
      headerTitleStyleSx={{ marginLeft: "10px" }}
      open={drawerStatus}
      onClose={onClose}
      onOpen={() => {}}
      secondaryButtonText="Cancel"
      onSecondaryButtonClick={onClose}
      primaryButtonText={primaryButtonText}
      isPrimaryButtonDisabled={!isDirty}
      isLoading={isAddCustomerSitesLoading || isUpdateCustomerSitesLoading}
      onPrimaryButtonClick={handleSitesSave}
      footer={haveFullAccess ? null : <></>}
    >
      <Stack sx={{ padding: "26px", gap: "20px" }}>
        <Stack gap="16px" direction="row" width="100%" justifyContent="end">
          <Typography variant="body-medium">Active</Typography>
          <CustomToggleButton
            name={"is_active"}
            isChecked={watchIsactive}
            isDisabled={!haveFullAccess || drawerData?.data?.meta?.is_default}
            onChange={(data) => {
              setValue("is_active", data?.value, { shouldDirty: true });
            }}
            // size="xs"
          />
        </Stack>

        <Stack sx={{ width: "100%", gap: "20px" }}>
          <Input
            name={"site_name"}
            label="Site Name*"
            control={control}
            // required={true}
            disabled={!haveFullAccess}
            error={errors?.site_name?.message ? true : false}
            errorMessage={errors?.site_name?.message}
          ></Input>
          <Stack gap="16px" direction="row" alignItems={"center"}>
            {/* check box */}
            <Tooltip
              title={!primaryContactMappingId ? "No Primary contact found" : ""}
              PopperProps={{
                sx: {
                  "& .MuiTooltip-tooltip": {
                    backgroundColor: theme.palette.neutral["B-015"],
                    ...theme.typography["body-small"],
                  },
                },
              }}
            >
              {/* <Box>
                <CustomCheckBox
                  checked={isContactPersonAsPrimaryContact}
                  disabled={
                    !haveFullAccess || (primaryContactMappingId ? false : true)
                  }
                  onClick={() => {
                    handleIsContactPersonAsPrimaryContact();
                  }}
                />
              </Box> */}
              <Box>
                <CustomCheckBox
                  checked={isContactPersonAsPrimaryContact}
                  disabled={!haveFullAccess}
                  onClick={() => {
                    handleIsContactPersonAsPrimaryContact();
                  }}
                />
              </Box>
            </Tooltip>
            <Typography variant="body-medium">
              {/* Contact person same as primary contact */}
              Same as customer primary contact
            </Typography>
          </Stack>
          <Input
            name={"contact_person"}
            label="Contact Person"
            disabled={!haveFullAccess || isContactPersonAsPrimaryContact}
            control={control}
            errorMessage={errors?.contact_person?.message}
          ></Input>
          <Input
            name={"email"}
            label="Email"
            disabled={!haveFullAccess || isContactPersonAsPrimaryContact}
            control={control}
            type="email"
            error={errors?.email?.message ? true : false}
            errorMessage={errors?.email?.message}
          ></Input>
          <Input
            fullWidth
            name={"contact_number"}
            label="Contact Number"
            disabled={!haveFullAccess || isContactPersonAsPrimaryContact}
            allowOnlyNumbers
            type="number"
            inputProps={{ maxLength: 15 }}
            control={control}
            error={errors?.contact_number ? true : false}
            errorMessage={errors?.contact_number?.message}
          ></Input>
          <Divider flexItem orientation="horizontal" />
          <Input
            name={"address_1"}
            control={control}
            disabled={!haveFullAccess}
            isResponsive={false}
            label="address 1*"
            sx={{ width: "100%" }}
            // @ts-ignore
            errorMessage={errors?.address_1?.message || ""}
            error={errors?.address_1 ? true : false}
          />
          <Input
            name={"address_2"}
            label="address 2"
            control={control}
            sx={{ width: "100%" }}
            disabled={!haveFullAccess}
            // @ts-ignore
            errorMessage={errors?.address_2?.message || ""}
            error={errors?.address_2 ? true : false}
          />
          <SelectField
            fullWidth
            name={"country"}
            label="Country"
            isSearchable
            keepMenuItemOriginalCasing
            // control={control}
            value={watch("country")}
            disabled={!haveFullAccess}
            options={countries}
            labelMappingKey="name"
            valueMappingKey="id"
            isResponsive={false}
            onValueChange={(data) => {
              setValue("country", data);
              setValue("state", "");
              setValue("city", "");
            }}
            sx={{ width: "100%" }}
            errorMessage={errors?.country && "Country is required"}
          />

          <SelectField
            name={"state"}
            label="State"
            isSearchable
            keepMenuItemOriginalCasing
            // control={control}
            disabled={!haveFullAccess}
            value={watch("state")}
            options={provinces}
            labelMappingKey="state_name"
            valueMappingKey="id"
            isResponsive={false}
            fullWidth
            onValueChange={(data) => {
              setValue("state", data);
              setValue("city", "");
            }}
            errorMessage={errors?.state && "State is required"}
          />
          <Input
            name={"city"}
            label="City"
            disabled={!haveFullAccess}
            control={control}
            isResponsive={false}
            fullWidth
            inputProps={{ maxLength: 50 }}
          />

          {/* <Input
            name={"street"}
            label="Street"
            disabled={!haveFullAccess || isContactPersonAsPrimaryContact}
            control={control}
            errorMessage={errors?.street?.message}
          ></Input> */}

          <Input
            name={"zip_code"}
            label="ZIP/Postal Code"
            disabled={!haveFullAccess}
            control={control}
            errorMessage={errors?.zip_code?.message}
            inputProps={{ maxLength: 25 }}
          ></Input>
        </Stack>
      </Stack>
    </Drawer>
  );
}

export default SiteAddDrawer;
